import {FC, useMemo} from 'react';
import {useDocumentContext} from '@/components/document/context';
import {ArrayGroupModal} from '@/components/document/group-modal-array';
import {BasicGroupModal} from '@/components/document/group-modal-basic';
import {isGroupVisuallyHidden} from '@/components/document/utils/is-group-visually-hidden';
import {DEFAULT_COLUMN_COUNT, DEFAULT_COLUMN_SPAN} from '@/constants/layout';
import {DocumentBaseGroup, DocumentField} from '@/engine';
import {FIELD_CATEGORIES, FIELD_TYPES} from '@/model/types';
import {getFieldCategoryType, isFieldInputType} from '@/model/utils/fields';
import SectionSubtitle from '../section-subtitle';
import {FieldGrid} from './field-grid';
import {FormField} from './form-field';
import {useDocumentMountEvent} from './hooks/use-document-mount-event';
import {useInspector} from './hooks/use-inspector';
import {UIField} from './ui-field';

type GroupProps = {
  sectionId: string;
  group: DocumentBaseGroup;
};

type FieldGridsProps = {
  group: DocumentBaseGroup;
  fields: DocumentField[];
  sectionId: string;
};

export const Group = ({sectionId, group}: GroupProps) => {
  useDocumentMountEvent(group?.onMount);

  const fields = group.fields.filter(field => field.visible);

  if (fields.length === 0) {
    return;
  }

  if (group.displayMode === 'modal' && group.id.includes('#')) {
    return <ArrayGroupModal group={group} fields={fields} sectionId={sectionId} />;
  }

  if (group.displayMode === 'modal') {
    return <BasicGroupModal group={group} fields={fields} sectionId={sectionId} />;
  }

  return <GroupFieldGrids sectionId={sectionId} group={group} fields={fields} />;
};

type GroupFieldGridsProps = {
  sectionId: string;
  group: DocumentBaseGroup;
  fields: DocumentField[];
};
const GroupFieldGrids: FC<GroupFieldGridsProps> = ({sectionId, group, fields}) => {
  const {valueResolver, visibilitySet} = useDocumentContext();
  const isDevInspectorVisible = useInspector(state => state.isVisible);
  const isGroupHidden = useMemo(() => isGroupVisuallyHidden(group), [group]);

  const dataTestId = group.id;

  const title = valueResolver(group.title, {visibilitySet});

  return (
    <div data-testid={dataTestId} className={isGroupHidden ? 'hidden' : undefined}>
      {title && (
        <div className="flex items-center gap-2">
          {isDevInspectorVisible && <span className="bg-violet/10 px-2 py-1 text-xs text-violet">{group.id}</span>}
          <SectionSubtitle data-testid={`${dataTestId}-title`}>{title}</SectionSubtitle>
        </div>
      )}

      <FieldGrids group={group} fields={fields} sectionId={sectionId} />
    </div>
  );
};

export const FieldGrids = ({group, fields, sectionId}: FieldGridsProps) => {
  const isDevInspectorVisible = useInspector(state => state.isVisible);

  const renderField = (field: DocumentField) => {
    const fieldCategory = getFieldCategoryType(field);

    const colSpan =
      field.layout?.colSpan ??
      (fieldCategory !== FIELD_CATEGORIES.UI
        ? (group.layout?.defaultColSpan ?? DEFAULT_COLUMN_SPAN)
        : (group.layout?.colCount ?? DEFAULT_COLUMN_COUNT));

    const hidden = field.type === FIELD_TYPES.HIDDEN;

    return (
      <FieldGrid.Cell
        key={field.id}
        colSpan={colSpan}
        fieldType={field.type}
        devHighlighted={isDevInspectorVisible && fieldCategory === FIELD_CATEGORIES.INPUT}
        devLabel={(isDevInspectorVisible && field.id) || undefined}
        hidden={hidden}
      >
        {isFieldInputType(field) ? <FormField sectionId={sectionId} field={field} /> : <UIField field={field} />}
      </FieldGrid.Cell>
    );
  };

  return <FieldGrid columnCount={group.layout?.colCount ?? DEFAULT_COLUMN_COUNT}>{fields.map(renderField)}</FieldGrid>;
};
